/**
 * Generated typed API client — compile-time-safe wrappers over {@link createFetcher}.
 *
 * ## Why this exists
 *
 * `openapi-typescript` generates a `paths` interface (per app: `src/types/types.gen.ts`)
 * describing every backend route, its request body, query params, and response
 * shape. Today that interface is types-ONLY: every fetch site still hand-writes
 * the URL string (`` `${API_BASE_URL}/api/calendar/` ``) and casts the response
 * (`json.data as CalendarEvent`). A wrong path or a wrong request shape is
 * therefore a *runtime* 404/422 — the exact Class-A/Class-B failure mode of the
 * 2026-07-18 calendar incident (Error KB 41cebbb9,
 * agents/analysis/api_404_prevention_observability_analysis_2026-07-18.md, item #11).
 *
 * `createTypedClient<paths>()` closes that gap. The path argument is constrained
 * to the literal keys of the generated `paths` type for the chosen HTTP method,
 * so a URL that does not exist on the backend is a **compile error** (Class A
 * becomes unrepresentable — analysis §6 Layer 0). The request body and query
 * params are typed from the OpenAPI operation, so a wrong-shaped payload is also
 * a compile error (helps Class B).
 *
 * ## Why over createFetcher (not a fresh fetch layer)
 *
 * The typed client wraps the axios instance produced by {@link createFetcher},
 * so it inherits — for free — the shared retry/error-normalisation AND the
 * caught-non-2xx observability hook (`reportFetchError` → `captureError`) that
 * Wave 1 added to the interceptor. A native-`fetch`-based generated client
 * (e.g. `openapi-fetch`) would bypass that interceptor and re-open GAP-1. Every
 * typed call is therefore automatically reported to `observability.runtime_errors`
 * on a 4xx/5xx, with zero per-call-site wiring.
 *
 * ## Codegen
 *
 * No extra codegen step is required beyond the existing per-app `npm run codegen`
 * (`openapi-typescript <spec> -o src/types/types.gen.ts`). This client is generic
 * over whatever `paths` type that command emits — regenerate the spec/types and
 * the client's compile-time surface updates automatically. See
 * `docs/frontend/apps/typed_api_clients.md`.
 *
 * ## Usage
 *
 * ```ts
 * import { createTypedClient } from "@plerity/data-access";
 * import type { paths } from "../types/types.gen";
 *
 * export const api = createTypedClient<paths>({
 *   baseURL: import.meta.env.VITE_API_URL ?? "",
 *   withCredentials: true,
 * });
 *
 * // ✓ path + body checked against the OpenAPI spec
 * const created = await api.post("/api/calendar/", { body: payload });
 *
 * // ✓ path params interpolated + type-checked
 * await api.del("/api/calendar/{event_id}", { params: { path: { event_id: 5 } } });
 *
 * // ✓ query params type-checked
 * await api.get("/api/calendar/", { params: { query: { source_id: 1 } } });
 *
 * // ✗ compile error — path not in the spec
 * await api.get("/api/calender/");
 * ```
 *
 * The response type is the OpenAPI success-response shape. Where the backend
 * response model is intentionally loose (a freeform `dict`, so the generated
 * type is `{ data?: { [k]: unknown }[] }`), cast the result to the app's
 * hand-written type at the call site — exactly the cast the raw-fetch code
 * already performs (`json.data as CalendarEvent`), but now with a
 * compile-checked URL and request body:
 * `const body = (await api.get("/api/calendar/")) as CalendarListBody;`
 *
 * (A response-override generic is intentionally NOT offered: TypeScript binds a
 * single explicit type argument to the *path* parameter — which must stay
 * inferred for path-safety — so an override generic and path inference are
 * mutually exclusive. openapi-fetch omits it for the same reason.)
 */

import type { AxiosInstance, AxiosRequestConfig } from "./fetcher";
import { createFetcher, type FetcherConfig } from "./fetcher";

// ============================================================================
// OpenAPI type extraction (types-only, zero runtime cost)
// ============================================================================

/** HTTP methods openapi-typescript emits on a path item. */
export type HttpMethod = "get" | "put" | "post" | "delete" | "patch" | "options" | "head";

/**
 * The subset of `Paths` keys that declare method `M`.
 *
 * openapi-typescript emits present methods as required members
 * (`post: operations["…"]`) and absent methods as optional `never`
 * (`post?: never`). An optional property is not a subtype of the required
 * `{ [K in M]: unknown }`, so absent-method paths resolve to `never` and are
 * filtered out — exactly the discrimination openapi-typescript-helpers uses.
 */
export type PathsWithMethod<Paths, M extends HttpMethod> = {
  [P in keyof Paths]: Paths[P] extends { [K in M]: unknown } ? P : never;
}[keyof Paths];

/** The operation object for `Paths[P][M]`, or `never` if the method is absent. */
export type OperationOf<Paths, P extends keyof Paths, M extends HttpMethod> = M extends keyof Paths[P]
  ? Paths[P][M]
  : never;

/** JSON request-body type declared by an operation (`never` when there is none). */
export type RequestBodyJSON<Op> = Op extends { requestBody?: infer RB }
  ? RB extends { content: { "application/json": infer B } }
    ? B
    : never
  : never;

/** True when the operation declares a *required* JSON request body. */
export type HasRequiredBody<Op> = Op extends { requestBody: { content: { "application/json": unknown } } }
  ? true
  : false;

/** Query-parameter object declared by an operation (`never` when there is none). */
export type QueryParams<Op> = Op extends { parameters: { query?: infer Q } }
  ? Q extends undefined
    ? never
    : Q
  : never;

/** Path-parameter object declared by an operation (`never` when there is none). */
export type PathParams<Op> = Op extends { parameters: { path?: infer P } } ? (P extends undefined ? never : P) : never;

/**
 * The `application/json` body of the first success response (200 → 201 → 204).
 * Falls back to `unknown` for operations whose success response has no typed
 * JSON body.
 */
export type SuccessResponseJSON<Op> = Op extends {
  responses: { 200: { content: { "application/json": infer T } } };
}
  ? T
  : Op extends { responses: { 201: { content: { "application/json": infer T } } } }
    ? T
    : Op extends { responses: { 204: unknown } }
      ? undefined
      : unknown;

// ============================================================================
// Request options (per-operation, precisely typed)
// ============================================================================

/** Options common to every method — path/query params + an axios escape hatch. */
type ParamOptions<Op> = ([QueryParams<Op>] extends [never] ? { query?: never } : { query?: QueryParams<Op> }) &
  ([PathParams<Op>] extends [never] ? { path?: never } : { path: PathParams<Op> });

/** Body slot — required when the operation mandates a JSON body, else optional. */
type BodyOptions<Op> =
  HasRequiredBody<Op> extends true
    ? { body: RequestBodyJSON<Op> }
    : [RequestBodyJSON<Op>] extends [never]
      ? { body?: never }
      : { body?: RequestBodyJSON<Op> };

/**
 * Full request-options object for an operation. `params` carries typed
 * `path`/`query`, `body` is the typed JSON payload, and `axios` is an escape
 * hatch for per-request headers, `signal`, `responseType`, etc.
 */
export type TypedRequestOptions<Op> = BodyOptions<Op> & {
  params?: ParamOptions<Op>;
  axios?: AxiosRequestConfig;
};

/**
 * Whether the caller *must* pass an options object (because the operation
 * requires a body or path params) — lets `get`/`delete` on a bare path stay
 * argument-free while `post` with a required body demands `{ body }`.
 */
export type OptionsArg<Op> =
  HasRequiredBody<Op> extends true
    ? [options: TypedRequestOptions<Op>]
    : [PathParams<Op>] extends [never]
      ? [options?: TypedRequestOptions<Op>]
      : [options: TypedRequestOptions<Op>];

// ============================================================================
// Client surface
// ============================================================================

type MethodFn<Paths, M extends HttpMethod> = <P extends PathsWithMethod<Paths, M>>(
  path: P,
  ...args: OptionsArg<OperationOf<Paths, P, M>>
) => Promise<SuccessResponseJSON<OperationOf<Paths, P, M>>>;

/**
 * A typed client keyed by an openapi-typescript `paths` interface. Each method
 * accepts only the paths that declare it, returns the operation's success
 * response type by default (overridable via the `T` generic), and interpolates
 * typed path params.
 */
export interface TypedClient<Paths> {
  get: MethodFn<Paths, "get">;
  post: MethodFn<Paths, "post">;
  put: MethodFn<Paths, "put">;
  patch: MethodFn<Paths, "patch">;
  /** DELETE — named `del` because `delete` is a reserved word. */
  del: MethodFn<Paths, "delete">;
  /** The underlying axios instance (shared retry/error/observability defaults). */
  instance: AxiosInstance;
}

// ============================================================================
// Runtime
// ============================================================================

/** Interpolate `{name}` segments in a path template with encoded param values. */
export function interpolatePath(template: string, pathParams?: Record<string, unknown>): string {
  if (!pathParams) return template;
  return template.replace(/\{([^}]+)\}/g, (_match, key: string) => {
    const value = pathParams[key];
    if (value === undefined || value === null) {
      throw new Error(`Missing path parameter "${key}" for "${template}"`);
    }
    return encodeURIComponent(String(value));
  });
}

interface RuntimeOptions {
  params?: { query?: Record<string, unknown>; path?: Record<string, unknown> };
  body?: unknown;
  axios?: AxiosRequestConfig;
}

async function dispatch(
  instance: AxiosInstance,
  method: HttpMethod,
  path: string,
  options?: RuntimeOptions
): Promise<unknown> {
  const url = interpolatePath(path, options?.params?.path);
  const response = await instance.request({
    method,
    url,
    params: options?.params?.query,
    data: options?.body,
    ...options?.axios,
  });
  return response.data;
}

/**
 * Build a {@link TypedClient} from an existing axios instance. Prefer
 * {@link createTypedClient} for normal use; this factory is exposed for tests
 * and for apps that already hold a bespoke `createFetcher` instance.
 */
export function createTypedClientFromInstance<Paths>(instance: AxiosInstance): TypedClient<Paths> {
  const method =
    (m: HttpMethod) =>
    (path: string, options?: RuntimeOptions): Promise<unknown> =>
      dispatch(instance, m, path, options);

  return {
    get: method("get"),
    post: method("post"),
    put: method("put"),
    patch: method("patch"),
    del: method("delete"),
    instance,
  } as TypedClient<Paths>;
}

/**
 * Create a typed API client keyed by an openapi-typescript `paths` interface,
 * backed by a {@link createFetcher} axios instance (shared retry, error
 * normalisation, and the caught-non-2xx observability hook).
 *
 * @typeParam Paths - the `paths` interface from the app's `types.gen.ts`.
 * @param config - same {@link FetcherConfig} accepted by `createFetcher`.
 */
export function createTypedClient<Paths>(config: FetcherConfig = {}): TypedClient<Paths> {
  return createTypedClientFromInstance<Paths>(createFetcher(config));
}
